import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";

const LucroPresumidoConfig = ({ onAlert, onConfigChange, initialData }) => {
  const [configs, setConfigs] = useState({
    // Configurações de ICMS
    icmsFreteChecked: false,
    icmsFretePercent: "",
    // Configurações de PIS/COFINS
    pisCofinsPercentage: "4",
    // Configurações difal
    diffal:
      "Será uma %(porcentagem) sobre o valor da venda, conforme o estado de destino.",
    // Dados do backend
    icmsAliquota: "",
    pisCofins: "",
  });

  // Efeito para carregar dados do backend sempre que mudarem
  useEffect(() => {
    if (initialData) {
      const savedConfig = localStorage.getItem(
        "tax_regime_config_lucro_presumido"
      );
      const localConfig = savedConfig ? JSON.parse(savedConfig) : {};

      setConfigs((prev) => ({
        ...prev,
        ...localConfig,
        // Dados do backend têm prioridade
        icmsAliquota: initialData.icmsAliquota?.toString() || "",
        pisCofins: initialData.pisCofins?.toString() || "",
        pisCofinsPercentage:
          initialData.pisCofins?.toString() ||
          localConfig.pisCofinsPercentage ||
          "4",
        icmsFreteChecked:
          initialData.icmsAliquotaSellerFreightEnabled || prev.icmsFreteChecked,
      }));
    }
  }, [initialData]);

  const activityTypes = [
    { value: "COMERCIO", label: "Comércio", percentage: "8" },
    { value: "INDUSTRIA", label: "Indústria", percentage: "8" },
    { value: "SERVICOS_GERAIS", label: "Serviços em Geral", percentage: "32" },
    {
      value: "SERVICOS_PROFISSIONAIS",
      label: "Serviços Profissionais",
      percentage: "32",
    },
    {
      value: "INTERMEDIACAO_NEGOCIOS",
      label: "Intermediação de Negócios",
      percentage: "32",
    },
    {
      value: "ADMINISTRACAO_LOCACAO",
      label: "Administração/Locação de Bens",
      percentage: "32",
    },
  ];

  // Função para registrar cliques no localStorage (robusta)
  const handleFieldClick = (field) => {
    try {
      const key = `lucroPresumidoConfig_clicks_${field}`;
      let count = 0;
      const stored = window.localStorage.getItem(key);
      if (stored !== null && !isNaN(Number(stored))) {
        count = Number(stored);
      }
      window.localStorage.setItem(key, String(count + 1));
    } catch (e) {}
  };
  const handleConfigChange = (field, value) => {
    const newConfigs = { ...configs, [field]: value };
    setConfigs(newConfigs);
    localStorage.setItem(
      "tax_regime_config_lucro_presumido",
      JSON.stringify(newConfigs)
    );
    // Notifica o componente pai sobre a alteração
    onConfigChange && onConfigChange();
    onAlert && onAlert("Configurações atualizadas com sucesso", "success");
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card
        sx={{
          border: "1px solid #e8eaed",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: "#ff7043",
              display: "flex",
              alignItems: "center",
              gap: 2,
              fontSize: "1.2rem",
              borderBottom: "2px solid #fff3e0",
              paddingBottom: 2,
            }}
          >
            ⚙️ Configurações do Lucro Presumido
          </Typography>

          {/* Seção não editável - apenas informativa */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              mb: 3,
            }}
          >
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "250px",
              }}
            >
              <TextField
                fullWidth
                label="ICMS Alíquota (%)"
                value={configs.icmsAliquota || ""}
                onChange={() => {}}
                type="number"
                size="small"
                helperText="Alíquota do ICMS configurada"
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f8f9fa",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "250px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <FormControl fullWidth size="small" disabled>
                  <InputLabel>Tipo de Atividade</InputLabel>
                  <Select
                    value={configs.activityType}
                    label="Tipo de Atividade"
                    onChange={() => {}}
                    onClick={() => handleFieldClick("activityType")}
                  >
                    {activityTypes.map((activity) => (
                      <MenuItem key={activity.value} value={activity.value}>
                        {activity.label} ({activity.percentage}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "250px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <TextField
                  fullWidth
                  label="Percentual de Presunção (%)"
                  value={configs.presumptionPercentage}
                  onChange={() => {}}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  size="small"
                  helperText="Definido pela atividade"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  disabled
                  onClick={() => handleFieldClick("presumptionPercentage")}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <TextField
                  fullWidth
                  label="IRPJ (%)"
                  value={configs.irpjRate}
                  onChange={() => {}}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  size="small"
                  helperText="Imposto de Renda"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  disabled
                  onClick={() => handleFieldClick("irpjRate")}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <TextField
                  fullWidth
                  label="CSLL (%)"
                  value={configs.csllRate}
                  onChange={() => {}}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  size="small"
                  helperText="Contribuição Social"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  disabled
                  onClick={() => handleFieldClick("csllRate")}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <TextField
                  fullWidth
                  label="PIS (%)"
                  value={configs.pisRate}
                  onChange={() => {}}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  size="small"
                  helperText="Cumulativo"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  disabled
                  onClick={() => handleFieldClick("pisRate")}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <TextField
                  fullWidth
                  label="COFINS (%)"
                  value={configs.cofinsRate}
                  onChange={() => {}}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  size="small"
                  helperText="Cumulativo"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  disabled
                  onClick={() => handleFieldClick("cofinsRate")}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <TextField
                  fullWidth
                  label="Adicional IRPJ (%)"
                  value={configs.additionalIrpj}
                  onChange={() => {}}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  size="small"
                  helperText="Para lucros acima de R$ 60.000/trimestre"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  disabled
                  onClick={() => handleFieldClick("additionalIrpj")}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "200px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <FormControl fullWidth size="small" disabled>
                  <InputLabel>Base de Presunção</InputLabel>
                  <Select
                    value={configs.presumptionBase}
                    label="Base de Presunção"
                    onChange={() => {}}
                    onClick={() => handleFieldClick("presumptionBase")}
                  >
                    <MenuItem value="REVENUE">Receita Bruta</MenuItem>
                    <MenuItem value="GROSS_PROFIT">Lucro Bruto</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Campos editáveis para regras do Lucro Presumido */}
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Seção PIS/COFINS */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#fff3e0",
                borderRadius: 2,
                border: "1px solid #ffcc80",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                PIS/COFINS
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "#666",
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    fontWeight: 500,
                  }}
                >
                  Configure a porcentagem de PIS/COFINS que será aplicada sobre
                  o valor das vendas
                </Typography>

                <TextField
                  fullWidth
                  label="% PIS/COFINS sobre a venda"
                  type="number"
                  size="small"
                  value={configs.pisCofinsPercentage || ""}
                  onChange={(e) =>
                    handleConfigChange("pisCofinsPercentage", e.target.value)
                  }
                  placeholder="Ex: 3.65"
                  InputProps={{
                    endAdornment: (
                      <Typography variant="caption" color="text.secondary">
                        %
                      </Typography>
                    ),
                  }}
                  helperText="Porcentagem de PIS/COFINS aplicada sobre todas as vendas"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#ff7043",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff7043",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#ff7043",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Seção Difal */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e8eaed",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                Difal
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: "#666",
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    fontWeight: 500,
                  }}
                >
                  Será uma %(porcentagem) sobre o valor da venda, conforme o
                  estado de destino.
                </Typography>
              </Box>
            </Box>

            {/* Seção ICMS do Produto */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#e8f5e8",
                borderRadius: 2,
                border: "1px solid #c8e6c9",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                ICMS do Produto
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Opções de Frete */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Tooltip
                    title="Marque se deseja somar frete grátis no ICMS do produto"
                    arrow
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        id="icmsFreteGratis"
                        checked={configs.icmsFreteChecked}
                        onChange={(e) =>
                          handleConfigChange(
                            "icmsFreteChecked",
                            e.target.checked
                          )
                        }
                        style={{ marginRight: 8, cursor: "pointer" }}
                      />
                      <label
                        htmlFor="icmsFreteGratis"
                        style={{
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          color: "#1a1a1a",
                          fontFamily:
                            '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        }}
                      >
                        Somar frete grátis no ICMS do produto
                      </label>
                    </Box>
                  </Tooltip>

                  <TextField
                    fullWidth
                    label="ICMS Alíquota (%)"
                    type="number"
                    size="small"
                    value={configs.icmsAliquota || ""}
                    onChange={(e) =>
                      handleConfigChange("icmsAliquota", e.target.value)
                    }
                    placeholder="Ex: 12.00"
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" color="text.secondary">
                          %
                        </Typography>
                      ),
                    }}
                    helperText="Alíquota do ICMS do produto"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#ff7043",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ff7043",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#ff7043",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LucroPresumidoConfig;
